import { api } from './client.js'
import { endpoints } from './endpoints.js'
import { unwrapList } from '../utils/format.js'

// Real PatientProfile shape:
//   { id, user_id, username, full_name, email, date_of_birth, sex,
//     phone_number, place_of_residence, patient_category,
//     emergency_contact_name, emergency_contact_phone, created_at, updated_at }
//
// The UI historically used { full_name, mrn, dob, sex, phone, province }.
// We add compatibility aliases so older screens still render.
//
// MRN (medical record number) format: "WTK-NNNNNN". Derived from the
// PatientProfile PK so every patient has a stable identifier without
// requiring a separate MRN column in the DB today. If we ever swap in a
// real government-issued UID (PNG NID, say), this is the one place to
// change it and every screen will pick up the new format.
function formatMrn(id) {
  if (id == null) return ''
  const n = String(id).padStart(6, '0')
  return `WTK-${n}`
}

function adaptPatient(p) {
  if (!p) return p
  return {
    ...p,
    mrn: formatMrn(p.id),
    dob: p.date_of_birth,
    phone: p.phone_number,
    province: p.place_of_residence,
  }
}

export { formatMrn }

export async function listPatients({ q, limit = 50 } = {}) {
  const params = { page_size: limit }
  if (q) params.search = q
  const { data } = await api.get(endpoints.patientList, { params })
  return unwrapList(data).map(adaptPatient)
}

export async function getPatient(id) {
  const { data } = await api.get(endpoints.patientDetail(id))
  return adaptPatient(data)
}

/**
 * Register a new patient.
 *
 * Doctors and admins are allowed to call this. The backend provisions a
 * backing auth User row (username derived from first+last if not given).
 * Unused fields can be left blank.
 *
 * @param {object} data
 * @param {string} data.first_name
 * @param {string} data.last_name
 * @param {string=} data.username_in     Optional custom username.
 * @param {string=} data.email_in        Optional email.
 * @param {string=} data.password        If blank, account gets unusable password.
 * @param {string}  data.sex             'male' | 'female' | 'other'
 * @param {string=} data.date_of_birth   ISO date, e.g. '1990-05-14'.
 * @param {string=} data.phone_number
 * @param {string=} data.place_of_residence
 * @param {string=} data.patient_category  Default 'general'.
 * @param {string=} data.emergency_contact_name
 * @param {string=} data.emergency_contact_phone
 */
export async function createPatient(data) {
  const { data: created } = await api.post(endpoints.patientList, data)
  return adaptPatient(created)
}

/**
 * Minimal list of active subscribing organisations for the doctor's
 * Register-patient employer dropdown: [{ id, name, org_type }, ...].
 * Backed by a doctor/admin-only endpoint that exposes only id + name +
 * type (not the full org record).
 */
export async function listEnrolmentOrgs() {
  const { data } = await api.get(endpoints.organizationEnrolmentOptions)
  return unwrapList(data)
}

/**
 * Update an existing patient's record. Demographics (DOB, sex, phone,
 * residential address, emergency contact) live on the PatientProfile;
 * identity fields write through to the backing User row: `first_name`,
 * `last_name` (display name) and `email_in` (email — deliberately NOT
 * uniqueness-gated, since families may share one address). Doctors and
 * admins may PATCH any patient in scope; the backend serializer treats
 * this as a partial update and only validates the fields supplied. Used by
 * the patient-detail "Edit details" form so records imported without a
 * DOB/sex (e.g. roster uploads) or with a typo'd name/email can be
 * corrected in-app — every identity change is captured in the audit log.
 */
export async function updatePatient(id, patch) {
  const { data } = await api.patch(endpoints.patientDetail(id), patch)
  return adaptPatient(data)
}

// ============================================================================
// Patient Identity Verification (V1)
//
// The Clinical Identification Photo is used by clinicians and provider staff
// to confirm the right patient at the point of care. The state machine on
// PatientProfile has six states:
//
//   unverified          - no photo, or attached but no self-signal
//   self_submitted      - patient uploaded a photo (auto-transition on upload)
//   telehealth_verified - clinician confirmed identity over video
//   clinician_verified  - clinician confirmed identity in person
//   document_verified   - identity confirmed against an official document
//   needs_review        - a clinician flagged the record for re-verification
//
// User-facing confidence tiers collapse these to three: High Confidence
// (clinician / document), Moderate (self_submitted / telehealth),
// Unverified (unverified / needs_review). See identity_confidence_display
// on the serialised payload — never hard-code the internal codes on the FE.
// ============================================================================

/**
 * Upload or replace the Clinical Identification Photo.
 *
 * Multipart PATCH — the backend auto-transitions the record to
 * `self_submitted` from `unverified`, and demotes a previously-verified
 * record back to `self_submitted` because the clinician verified the OLD
 * photo, not this one. On success the fresh serialised patient is returned
 * so the caller can render the new confidence badge without a round-trip.
 *
 * `file` must be a browser File (from an <input type=file> or canvas
 * toBlob). Backend accepts any image type Pillow can read; JPEG/PNG is
 * safest for cross-device previews.
 */
export async function uploadIdentificationPhoto(id, file) {
  const form = new FormData()
  form.append('identification_photo', file)
  const { data } = await api.patch(endpoints.patientDetail(id), form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return adaptPatient(data)
}

/**
 * Mark identity as verified by a clinician.
 *
 * Doctor / specialist / platform-admin only. Records:
 *   identity_verification_status = <method>
 *   identity_verified_by         = request.user
 *   identity_verified_at         = now
 * Clears any prior identity_flag_reason.
 *
 * `method` defaults to `clinician_verified` (in-person is the common case).
 * Pass `telehealth_verified` from a video consult verification, or
 * `document_verified` when a photo ID has been sighted.
 *
 * Errors:
 *   400 if the patient has no photo attached — nothing to verify.
 *   400 on an unknown method value.
 *   403 if the caller is not a clinician / admin.
 */
export async function verifyPatientIdentity(id, { method = 'clinician_verified' } = {}) {
  const { data } = await api.post(endpoints.patientVerifyIdentity(id), { method })
  return adaptPatient(data)
}

/**
 * Flag the record as "Identity requires verification".
 *
 * Any clinician who suspects the identity is wrong (mismatched photo,
 * duplicate record, name discrepancy, etc.) taps this. Moves the record
 * to `needs_review` and records a structured reason for the audit trail.
 * Prior verified-by / verified-at are preserved so downstream UIs can
 * show "previously verified by X on Y" alongside the fresh flag.
 *
 * `reason` MUST be one of:
 *   photo_outdated | photo_mismatch | duplicate_suspected |
 *   name_discrepancy | other
 */
export async function flagPatientIdentity(id, reason) {
  const { data } = await api.post(endpoints.patientFlagIdentity(id), { reason })
  return adaptPatient(data)
}
